import React, { ChangeEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { ILiveClass } from 'types/liveClass';
import { useHistory } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDeleteLiveClass } from 'hooks/liveClasses';
import CircularProgress from '@mui/material/CircularProgress';
import DataTable, { TableColumn } from 'react-data-table-component';

interface ILiveClassesListProps {
  liveClassesList: ILiveClass[];
  loading: boolean;
  error: any;
  refetch: () => any;
}

const LiveClassesList = ({ liveClassesList, loading, error, refetch }: ILiveClassesListProps) => {
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState('');

  const { mutateAsync: deleteLiveClass, isLoading: isDeleting } = useDeleteLiveClass();

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value.toLowerCase();
    setSearchQuery(keyword);
  };

  const handleRowClicked = (row: ILiveClass) => {
    history.push(`/dashboard/edit-live-class/${row._id}`);
  };

  const handleDeleteRow = async (row: ILiveClass) => {
    try {
      if (!row._id) return;
      const response = await deleteLiveClass(row._id);

      if (response.data) {
        toast.success('Live class deleted successfully');
      }
    } catch (error) {
      toast.error('Error deleting live class');
    }
  };

  const columns = [
    {
      name: 'Session Title',
      selector: (row: ILiveClass) => row.basicSessionId.title,
      sortable: true,
      grow: 1,
    },
    {
      name: 'Instructor Name',
      selector: (row: ILiveClass) => row.instructorName,
      sortable: true,
    },
    {
      name: 'Link',
      selector: (row: ILiveClass) => row.link,
    },
    {
      name: 'Action',
      grow: 2,
      cell: (row: ILiveClass) => (
        <div className="btn-group text-nowrap" role="group" aria-label="Basic example">
          <button
            type="button"
            style={{ fontSize: '14px' }}
            className="btn btn-warning me-2 "
            onClick={() => handleRowClicked(row)}
            disabled={row.basicSessionId.status === 'completed'}
          >
            Edit
          </button>
          <button
            type="button"
            style={{ fontSize: '14px' }}
            className="btn btn-danger me-2"
            onClick={() => handleDeleteRow(row)}
            disabled={row.basicSessionId.status === 'completed'}
          >
            Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ].filter(Boolean) as TableColumn<any>[];

  const customStyles = {
    headRow: {
      style: {
        minWidth: '100%',
      },
    },
    headCells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    cells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    search: {
      style: {
        position: 'relative',
        width: '100%',
      },
    },
    searchInput: {
      style: {
        width: '100%',
      },
    },
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <div className="d-flex align-items-center w-100">
        <input
          type="text"
          placeholder="Search sessions..."
          onChange={handleSearch}
          className="form-control me-2"
          value={searchQuery}
        />
        <div className="cursor-pointer" onClick={() => setSearchQuery('')}>
          <RefreshIcon />
        </div>
      </div>
    );
  }, [searchQuery]);

  return (
    <div className="w-100">
      {error && (
        <div>
          <p>Error fetching session data</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 btn btn-primary btn-block w-100 d-flex align-items-center justify-content-center"
          >
            Refetch
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={30} />
        </div>
      ) : (
        <DataTable
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          data={liveClassesList}
          columns={columns}
          pagination
          paginationPerPage={10}
          customStyles={customStyles}
          onRowClicked={handleRowClicked}
          highlightOnHover
          pointerOnHover
          paginationComponentOptions={{
            noRowsPerPage: true,
          }}
          disabled={isDeleting}
        />
      )}
    </div>
  );
};

export default LiveClassesList;
